import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { fetchAdminApps } from '@/api/console';

/**
 * EAA-1814: "Ứng dụng của bạn" augmentation — appends a DIMMED, non-clickable
 * card for every registry app the caller has registered but not yet approved
 * (`enabled === false`). Never hide it: "the operator needs to see that it
 * exists" (handoff, S2). A disabled app never reaches a plain user's own
 * `config.apps` launcher grid in the first place (enabling an app IS the
 * production approval gate — shell rule, S1) — so this is additive to the
 * existing grid, gated on the registry-view capability, and changes nothing
 * about what a plain user without that capability already sees.
 *
 * Shares its query key with `HomeKpiStrip`/`HomeActionQueue` — one fetch of
 * `/admin/apps` backs all three regions.
 */
export function HomeAppsPending({
    can,
    apiBase,
    accessToken,
    authenticated,
}: {
    can: (key: string) => boolean;
    apiBase: string;
    accessToken: string | null;
    authenticated: boolean;
}) {
    const { t } = useTranslation();
    const canApps = can('console.apps.view');

    const appsQuery = useQuery({
        queryKey: ['home-admin-apps', accessToken],
        queryFn: () => fetchAdminApps(apiBase, accessToken as string),
        enabled: authenticated && Boolean(accessToken) && canApps,
        retry: false,
    });

    if (!canApps || !appsQuery.isSuccess) {
        return null;
    }

    const pending = appsQuery.data.filter((app) => !app.enabled);
    if (pending.length === 0) {
        return null;
    }

    return (
        <>
            {pending.map((app) => (
                <div
                    key={app.code}
                    className="portal-launcher-card portal-home-app-card--pending"
                    aria-disabled="true"
                >
                    <span className="portal-launcher-card-icon" aria-hidden="true">
                        <PendingAppIcon />
                    </span>
                    <strong>{app.name}</strong>
                    <span className="portal-home-app-card-pending-note">
                        {t('home.appsPending.pendingNote')}
                    </span>
                </div>
            ))}
        </>
    );
}

function PendingAppIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            aria-hidden="true"
        >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

export default HomeAppsPending;
